import { dayMonth, letterDates, todayIso, useFutureLetters, useLetterActions } from '@shkola/api';
import { EnvelopeSimpleIcon } from '@phosphor-icons/react/EnvelopeSimple';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { EmptyState } from '../components/ui';
import {
  Block,
  DARK,
  GRAD,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  grad,
  pillClass,
} from '../design/forms';
import { CheckCircleIcon } from '../design/icons';
import { LifeArt, Sub } from './LifeArt';

/*
 * «Лист собі в майбутнє» in «Форми 2» (28.10). The title on the paper and what the capsule is under it;
 * on the left the letter to write — a big white field on a soft butter card with the envelope quiet
 * behind, when to open it as three choices (the chosen one ringed, as the faces in the settings), the
 * dark pill «Запечатати»; on the right one's letters — the sealed ones as envelopes with their day, an
 * opened one as a white sheet to read.
 */

const fullDate = (iso: string) => `${dayMonth(iso)}.${iso.slice(0, 4)}`;
const WHEN = ['nextYear', 'yearEnd', 'graduation'] as const;

/**
 * «Лист собі в майбутнє»: a time capsule. The student writes and seals; the text comes back only
 * on the chosen day — the database itself keeps it closed till then.
 */
export function LettersPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const letters = useFutureLetters();
  const actions = useLetterActions();
  const today = todayIso();
  const dates = letterDates(today, ctx.kind === 'student' ? ctx.child.class?.grade : undefined);
  const [body, setBody] = useState('');
  const [when, setWhen] = useState<keyof typeof dates>('graduation');
  const [sealed, setSealed] = useState<string>();
  if (ctx.kind !== 'student') return <EmptyState>{t('letters.empty')}</EmptyState>;
  const list = letters.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('letters.title')}
        sub={<span className="block max-w-2xl">{t('letters.intro')}</span>}
      />
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_400px]">
        <Block>
          {/* the letter on a soft card, the envelope quiet behind */}
          <div
            className="relative isolate overflow-hidden rounded-[24px] p-3 pt-4 md:p-4"
            style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
          >
            <LifeArt
              kind="letter"
              tone="butter"
              size={150}
              ghost
              className="pointer-events-none absolute -top-6 -right-4 -z-10"
            />
            <p className="mb-3 px-1 text-[13px] font-medium">{t('letters.hand')}</p>
            <textarea
              aria-label={t('letters.title')}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              maxLength={5000}
              placeholder={t('letters.placeholder')}
              className="block w-full resize-none rounded-[18px] bg-white p-5 text-[17px] leading-7 outline-none placeholder:text-[rgb(31_26_53/0.42)] focus:ring-2 focus:ring-[var(--sh-dark)]"
              style={{ color: INK }}
            />
          </div>

          <div className="mt-5">
            <Sub>{t('letters.when')}</Sub>
            <div
              role="radiogroup"
              aria-label={t('letters.when')}
              className="grid grid-cols-1 gap-2 sm:grid-cols-3"
            >
              {WHEN.map((key) => {
                const on = when === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setWhen(key)}
                    className="flex flex-col items-start rounded-[18px] px-4 py-3 text-left transition-colors"
                    style={{
                      backgroundColor: on ? '#FFFFFF' : PAPER,
                      boxShadow: on ? `inset 0 0 0 2px ${DARK}` : undefined,
                    }}
                  >
                    <span className="text-[13px] leading-[18px]" style={{ color: INK_SOFT }}>
                      {t(`letters.${key}`)}
                    </span>
                    <span
                      className="font-display text-[24px] leading-8 font-light tracking-[-0.02em]"
                      style={{ color: INK }}
                    >
                      {fullDate(dates[key])}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={!body.trim() || actions.write.isPending}
              onClick={() =>
                actions.write.mutate(
                  { schoolId: ctx.schoolId, body, openOn: dates[when] },
                  {
                    onSuccess: () => {
                      setSealed(dates[when]);
                      setBody('');
                    },
                  },
                )
              }
              className={`${pillClass('dark')} disabled:opacity-35`}
            >
              {t('letters.seal')}
              <PillIcon icon={EnvelopeSimpleIcon} />
            </button>
            {sealed && (
              <p
                className="motion-rise inline-flex items-center gap-2 text-[15px] font-medium"
                style={{ color: GRAD.mint.ink }}
              >
                <CheckCircleIcon size={18} weight="fill" aria-hidden />
                {t('letters.sealed', { date: fullDate(sealed) })}
              </p>
            )}
          </div>
        </Block>

        <Block>
          <Head title={t('letters.mine')} count={list.length} />
          {letters.isPending ? (
            <div
              className="h-[160px] animate-pulse rounded-[22px]"
              style={{ backgroundColor: PAPER }}
              aria-busy
            />
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <LifeArt kind="letter" tone="butter" size={80} />
              <p className="mt-3 text-[15px]" style={{ color: INK_SOFT }}>
                {t('letters.empty')}
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {list.map((l) =>
                l.opened ? (
                  <li
                    key={l.id}
                    className="rounded-[20px] p-4"
                    style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
                  >
                    <p className="text-[12.5px] font-medium">
                      {t('letters.written', { date: fullDate(l.writtenOn) })} ·{' '}
                      {t('letters.openedOn', { date: fullDate(l.openOn) })}
                    </p>
                    <p
                      className="mt-2 rounded-[16px] bg-white p-4 text-[16px] leading-[26px] whitespace-pre-wrap"
                      style={{ color: INK }}
                    >
                      {l.body}
                    </p>
                  </li>
                ) : (
                  <li
                    key={l.id}
                    className="flex items-center gap-3 py-2.5"
                    style={{ borderTop: `1px solid ${LINE}` }}
                  >
                    <LifeArt kind="letter" tone="butter" size={44} />
                    <span className="min-w-0 flex-1">
                      <span
                        className="block text-[15px] leading-5 font-medium"
                        style={{ color: INK }}
                      >
                        {t('letters.sealedUntil', { date: fullDate(l.openOn) })}
                      </span>
                      <span className="block text-[12.5px]" style={{ color: INK_FAINT }}>
                        {t('letters.written', { date: fullDate(l.writtenOn) })}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => actions.discard.mutate(l.id)}
                      className={`${pillClass('light', 'sm')} pr-3.5`}
                    >
                      {t('letters.discard')}
                    </button>
                  </li>
                ),
              )}
            </ul>
          )}
        </Block>
      </div>
    </div>
  );
}
